import { useState } from 'react';
import { api, ServerError } from './api';
import { fa } from './fa';

/*
 * Signing the cashier in.
 *
 * SMS one-time code, the same as the panel — a shop tablet is shared, gets left
 * on a counter, and a stored password on it is a password the whole street has.
 *
 * The design's shift screen shows a 4-digit PIN, and that is the right shape
 * for the *second* cashier of the day: a fast local switch on an already-
 * authenticated device. It cannot be the first authentication, because a PIN
 * validated on the tablet is a PIN anyone holding the tablet can brute-force in
 * an afternoon. So: SMS to open the device, PIN to swap the person at it.
 */
export function Login({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'phone' | 'code'>('phone');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true); setError(null);
    try {
      await api.post('/auth/request-code', { phone });
      setStage('code');
    } catch (e) {
      setError(e instanceof ServerError ? (e.detail ?? e.message) : 'ارسال کد ناموفق بود');
    } finally { setBusy(false); }
  }

  async function verify() {
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ token: string }>('/auth/verify-code', { phone, code });
      localStorage.setItem('luma.till.token', res.token);
      onDone();
    } catch (e) {
      setError(e instanceof ServerError ? (e.detail ?? e.message) : 'کد نادرست است');
    } finally { setBusy(false); }
  }

  const value = stage === 'phone' ? phone : code;
  const setValue = stage === 'phone' ? setPhone : setCode;
  const ready = stage === 'phone' ? phone.length >= 10 : code.length >= 4;

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh', background: 'var(--luma-surface-desk)',
        display: 'grid', placeItems: 'center', padding: 28,
      }}
    >
      <div
        style={{
          width: 520, background: 'var(--luma-card)', borderRadius: 12,
          border: '1px solid var(--pos-rule-frame)', padding: 28,
          boxShadow: '0 12px 32px rgba(32,30,29,.22)',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="logo.png"
            alt=""
            width={38}
            height={38}
            style={{ borderRadius: 11, display: 'block' }}
          />
          <div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>صندوق لوما استور</div>
            <div style={{ fontSize: 13, color: 'var(--luma-muted)' }}>
              {stage === 'phone' ? 'شماره موبایل صندوق‌دار' : `کد ارسال‌شده به ${fa(phone)}`}
            </div>
          </div>
        </div>

        <input
          value={value}
          /*
           * Capped at the length each stage actually needs.
           *
           * Without it the field happily accepts twenty digits, which is what
           * happens when a cashier taps twice on a laggy tablet: the number
           * looks plausibly long, the request is rejected, and nothing on
           * screen explains why.
           */
          onChange={(e) =>
            setValue(e.target.value.replace(/[^\d]/g, '').slice(0, stage === 'phone' ? 11 : 6))
          }
          inputMode="numeric"
          onKeyDown={(e) => { if (e.key === 'Enter' && ready && !busy) (stage === 'phone' ? send() : verify()); }}
          placeholder={stage === 'phone' ? '۰۹۱۲۳۴۵۶۷۸۹' : 'کد ۶ رقمی'}
          autoFocus
          className="pos-num"
          style={{
            height: 66, borderRadius: 12, border: '1px solid var(--luma-line-strong)',
            background: 'var(--luma-sunken)', padding: '0 18px', fontSize: 24, fontWeight: 700,
            letterSpacing: '.06em',
            boxShadow: '0 0 0 4px rgba(249,124,21,.10)',
          }}
        />

        {error && (
          <div
            style={{
              background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
              borderRadius: 10, padding: '10px 14px', fontSize: 13.5,
              color: 'var(--luma-tint-danger-text)', fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        <button
          onClick={() => (stage === 'phone' ? send() : verify())}
          disabled={!ready || busy}
          style={{
            height: 60, borderRadius: 12, fontWeight: 800, fontSize: 16,
            background: ready && !busy ? 'var(--luma-accent)' : 'var(--pos-disabled)',
            color: '#FFF',
          }}
        >
          {busy ? 'لطفاً صبر کنید…' : stage === 'phone' ? 'ارسال کد' : 'ورود به صندوق'}
        </button>

        {stage === 'code' && (
          <button
            onClick={() => { setStage('phone'); setCode(''); setError(null); }}
            style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-accent-hover)' }}
          >
            تغییر شماره
          </button>
        )}
      </div>
    </div>
  );
}
